import {
    NinchatCannedMessage, NinchatCannedMessageLanguages,
} from 'api/types/properties'
import {CannedMessageKey} from '../../components/SendCannedMessages/types'
import {NinchatProperties} from 'api/types'
import type {TFunction} from 'i18next'

export const sortCannedMessages = (cannedMessages: NinchatCannedMessage[]): NinchatCannedMessage[] => {
    const D = new Set()

    const sorted = [...cannedMessages]
        .sort((a, b) => {
            const [A] = a.keywords
            const [B] = b.keywords
            return A < B ? 1 : A > B ? -1 : 0
        })
        .sort((a, b) => {
            const [A] = a.keywords
            const [B] = b.keywords
            const Ax = A.split('/')
            const Bx = B.split('/')

            Ax.reduce((p, c) => {
                D.add(p)
                return [p, c].join('/')
            })

            Bx.reduce((p, c) => {
                D.add(p)
                return [p, c].join('/')
            })

            return 1
        })
        .sort((a, b) => {
            const [A] = a.keywords
            const [B] = b.keywords
            const Ax = A.split('/')
            const Bx = B.split('/')
            const Al = Ax.length
            const Bl = Bx.length
            const An = Ax.slice(0, Al - 1).join('/')
            const Bn = Bx.slice(0, Bl - 1).join('/')
            const Ad = D.has(A) ? A : D.has(An) ? An : null
            const Bd = D.has(B) ? B : D.has(Bn) ? Bn : null

            if (Ad === Bd) {
                return A < B ? -1 : 1
            }

            if (Ad === null) {
                return 1
            }

            if (Bd === null) {
                return -1
            }

            if (Bd.startsWith(Ad)) {
                if (B.startsWith(A)) {
                    return -1
                }
                return 1
            }

            if (Ad.startsWith(Bd)) {
                if (A.startsWith(B)) {
                    return 1
                }
                return -1
            }

            return -1
        })

    return sorted
}

export const sortCannedMessagesLanguages = (languages: NinchatCannedMessageLanguages, t: TFunction, cannedMessagesKey?: string): NinchatCannedMessageLanguages => {
    const sortedLanguages = languages.sort((left, right) => {
        // Sort by isDefault first, then by translated name
        if (left.isDefault && !right.isDefault) {
            return -1
        }
        if (!left.isDefault && right.isDefault) {
            return 1
        }
        const tLeft = t(left.name, t)
        const tRight = t(right.name, t)
        return tLeft < tRight ? -1 : tLeft > tRight ? 1 : 0
    })
    // if cannedMessagesKey is provided, that language is always at the top
    if (cannedMessagesKey) {
        const defaultLanguage = sortedLanguages.find((lang) => lang.id === cannedMessagesKey)
        if (defaultLanguage) {
            return [defaultLanguage, ...sortedLanguages.filter((lang) => lang.id !== defaultLanguage.id)]
        }
    }
    return sortedLanguages
}

// normalizes the supported canned message languages to make sure
// 1. Finnish and Swedish are always included.
// 2. Finnish is always the default language if no other default language is set.
export const normalizeCannedMessageLanguages = (languages: NinchatProperties.NinchatCannedMessageLanguages): NinchatProperties.NinchatCannedMessageLanguages => {
    const defaultLanguages = [
        {
            id: 'fin-2014',
            isDefault: false,
            name: 'Finnish',
        },
        {
            id: 'swe-6444',
            isDefault: false,
            name: 'Swedish',
        },
        {
            id: 'eng-1918',
            isDefault: false,
            name: 'English',
        },
    ]

    const retval = [...languages]
    for (const language of defaultLanguages) {
        if (!retval.some((lang) => lang.id === language.id)) {
            retval.push({...language})
        }
    }
    const hasDefault = retval.some((lang) => lang.isDefault)
    if (!hasDefault) {
        const indx = retval.findIndex((lang) => lang.id === defaultLanguages[0].id)
        if (indx !== -1) {
            retval[indx] = {
                ...retval[indx],
                isDefault: true,
            }
        }
    }

    return retval
}


/** *
 * prefill the translation data
 *    - message with default language ( fi )
 *    - sv with swedish language
 *    - en with english language
 ***/

export const backfillCannedMessageTranslations = (cannedMessage?: NinchatCannedMessage): NinchatCannedMessage['translations'] => {
    if (!cannedMessage) {
        return {}
    }
    const {
        message = '',
        sv = '',
        en = '',
        translations = {},
    } = cannedMessage
    const legacyFieldMappings = [
        {
            content: message,
            languageId: 'fin-2014',
        },
        {
            content: sv,
            languageId: 'swe-6444',
        },
        {
            content: en,
            languageId: 'eng-1918',
        },
    ]

    const updatedTranslations = {...translations}
    legacyFieldMappings.forEach(({
        content, languageId,
    }) => {
        // Only backfill if the language is supported and translation doesn't exist yet
        if (!(languageId in updatedTranslations) && content && content.length > 0) {
            updatedTranslations[languageId] = content
        }
    })

    return updatedTranslations
}


export const getDefaultMessage = (languages: NinchatCannedMessageLanguages, cannedMessage?: NinchatCannedMessage): string => {
    if (!cannedMessage) {
        return ''
    }


    const languageId = languages.find((lang) => lang.isDefault)?.id ?? 'fin-2014'
    const message = cannedMessage.translations?.[languageId] ?? ''
    const translations = cannedMessage.translations ?? {}
    if (languageId in translations) {
        return message
    }

    // only look for default messages if they are either 'fin-2014'(message), 'swe-6444'(se) or 'eng-1918' (en)
    switch (languageId) {
        case 'fin-2014':
            return cannedMessage.message ?? ''
        case 'swe-6444':
            return cannedMessage.sv ?? ''
        case 'eng-1918':
            return cannedMessage.en ?? ''
        default:
        // If the languageId is not one of the expected ones, return an empty string
            return ''
    }
}

export const translate = (name: string, t: TFunction): string => {
    // TODO - for language drop down, currently we only support translations for those three countries
    const basicTranslations = {
        English: t('English'),
        Finnish: t('Finnish'),
        Swedish: t('Swedish'),
    } as Record<string, string>

    return basicTranslations[name] ?? name
}

export const hasParent = (message: NinchatCannedMessage): boolean => {
    if (message?.keywords?.length === 0) {
        return false
    }

    const nodes = message.keywords?.[0]?.split('/') ?? []
    return nodes.length > 1
}

export const isParentOf = (parent: NinchatCannedMessage, child: NinchatCannedMessage): boolean => child.keywords[0].startsWith(`${parent.keywords[0]}/`)

// check if the canned messages are equal -> simple string comparison
export const isCannedMsgEqual = (nodeLeft: NinchatCannedMessage, nodeRight: NinchatCannedMessage): boolean => nodeLeft.keywords[0] === nodeRight.keywords[0]

export const isRootCannedMessageKeyword = (keyword: string): boolean => keyword.length > 0 && !keyword.includes('/')

export const getValidSharedCannedMessageRoots = (sharedCannedMessages?: string[]): string[] | undefined => {
    if (sharedCannedMessages === undefined) {
        return undefined
    }

    return [...new Set(sharedCannedMessages.filter(isRootCannedMessageKeyword))]
}

export const filterSharedCannedMessages = (
    cannedMessages: NinchatCannedMessage[],
    queueID: NinchatProperties.QueueID,
    sharedCannedMessages?: string[],
): NinchatCannedMessage[] => {
    const validSharedCannedMessages = getValidSharedCannedMessageRoots(sharedCannedMessages)

    if (validSharedCannedMessages !== undefined) {
        return cannedMessages.filter((cannedMessage) => validSharedCannedMessages.some((selectedKeyword) => {
            const selectedCannedMessage = {
                keywords: [selectedKeyword],
                message: '',
            }

            return isCannedMsgEqual(cannedMessage, selectedCannedMessage) || isParentOf(selectedCannedMessage, cannedMessage)
        }))
    }

    return cannedMessages.filter((cannedMessage) => cannedMessage
        .allowedQueues?.some((currQueueId) => currQueueId === queueID))
}

// get the translated canned message text based for given cannedMessageKey
export const getCannedMessageText = (m: NinchatCannedMessage, cannedMessageKey: CannedMessageKey) => m.translations?.[cannedMessageKey] ?? ''

export const hasChildren = (cm: NinchatCannedMessage, cannedMessages: NinchatCannedMessage[], cannedMessageKey: string): boolean => cannedMessages.some((m: NinchatCannedMessage) => {
    const text = getCannedMessageText(m, cannedMessageKey)
    return isParentOf(cm, m) && text && text.length > 0
})

export const getCannedMessagesKey = (languages: NinchatCannedMessageLanguages = [], uiCannedMessagesKey?: string): string => {
    // if ui canned messages key is message, means we are moving from old to new canned messages. And in old canned messages, 'message' -- always --> 'fi', 'se' -> 'swe-6444', 'en' -> 'eng-1918'
    if (uiCannedMessagesKey === 'message') {
        return 'fin-2014'
    } else if (uiCannedMessagesKey === 'sv') {
        return 'swe-6444'
    } else if (uiCannedMessagesKey === 'en') {
        return 'eng-1918'
    }
    const defaultLanguage = languages?.find((lang) => lang.isDefault)
    const defaultLanguageKey = defaultLanguage?.id ?? 'fin-2014' // Default to Finnish if no default language is set
    // Validate that the selected key exists in available languages
    const isValidLanguageKey = uiCannedMessagesKey && languages?.some((lang) => lang.id === uiCannedMessagesKey)
    return isValidLanguageKey ? uiCannedMessagesKey : defaultLanguageKey
}


export const mergeCannedMessages = ({
    organizationCannedMessages = [], userCannedMessages = [],
}:{
    organizationCannedMessages: Array<NinchatCannedMessage>, userCannedMessages: Array<NinchatCannedMessage>,
}): NinchatCannedMessage[] => {
    const filteredOrgMessages = organizationCannedMessages.filter((orgMsg) => !userCannedMessages.some((userMsg) => isCannedMsgEqual(userMsg, orgMsg) || isParentOf(userMsg, orgMsg)))
    return [...sortCannedMessages(filteredOrgMessages?.map((cm) => ({
        ...cm,
        shared: true,
    })) ?? []), ...userCannedMessages]
}
